import { useEffect } from 'react';
import { AsyncRenderer } from '@client/new/components/AsyncRenderer/AsyncRenderer';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import { useGetInfluencerQuery } from 'generated/graphql';
import auth from '@client/components/auth';
import { PATHS } from '@client/routes';
import { navigate } from '@client/new/helpers/history';
import { useOnboardingViewModel } from './view-model/Onboarding.viewModel';
import { isEligibleForOnboarding } from './model/onboardingEligibility';
import { OnboardingView } from './view/Onboarding.view';
import { OnboardingLoader } from './view/components/OnboardingLoader';

export const OnboardingPage = () => {
  const influencerId = useInfluencerId();
  const influencerQuery = useGetInfluencerQuery({
    variables: { id: influencerId },
    skip: !influencerId,
  });

  const creatorCreatedAt = influencerQuery.data?.creator?.createdAt ?? null;
  const homePath = auth.isInfluencer()
    ? PATHS.HOME
    : PATHS.HOME_CREATOR(influencerId);

  const vm = useOnboardingViewModel(creatorCreatedAt);

  // Lock completed onboarding: once a creator has finished (strategy selected
  // or dismissed), returning to an answer-input step must not let them
  // re-answer and re-roll the result/booking. The result, strategy, book-call,
  // and calculating steps are intentionally excluded so a completed creator can
  // still review their result and change their strategy, and the post-selection
  // booking flow on this same route is never interrupted.
  const shouldRedirectCompletedToHome =
    vm.type === 'fetched' &&
    vm.value.hasCompletedOnboarding &&
    vm.value.isAnswerEditingStep;

  useEffect(() => {
    if (!influencerQuery.data) return;

    if (!isEligibleForOnboarding(creatorCreatedAt)) {
      navigate(homePath);
    }
  }, [influencerQuery.data, creatorCreatedAt, homePath]);

  useEffect(() => {
    if (shouldRedirectCompletedToHome) {
      navigate(homePath);
    }
  }, [shouldRedirectCompletedToHome, homePath]);

  return (
    <AsyncRenderer
      data={vm}
      loadRender={<OnboardingLoader />}
      renderer={(data) => <OnboardingView {...data} />}
    />
  );
};
